/**
 * Public Landing Page — trang chủ công khai.
 *
 * Style: giữ STRUCTURE của marketingarena.vn (top nav · hero · stats · overview ·
 * timeline · rules · awards · partners · contact · footer) NHƯNG dùng nhận diện
 * thương hiệu ICFGL UEL — sáng/trắng, navy + hot pink accent, sans-serif uppercase,
 * minimalist academic, không tech-dark.
 *
 * Nội dung lấy từ src/config/siteContent.ts.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Gavel,
  Login,
  HowToReg,
  OpenInNew,
  EmojiEvents,
  ArrowDownward,
  AccountBalance,
  Diversity3,
  Menu as MenuIcon,
  Close as CloseIcon,
  Article,
  Groups,
  Schedule,
  Mail,
  Visibility,
  CheckCircle,
  ExpandMore,
  Search as SearchIcon,
  Facebook,
  Public as PublicIcon,
  QrCode2,
  WorkspacePremium,
  PlayCircleOutline,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { siteContent } from '../config/siteContent'
import { brand, gradientBrand, gradientBackdrop } from '../theme/brand'
import { useAppSelector } from '../hooks/redux'
import LanguageSwitcher from '../components/LanguageSwitcher'
import {
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
} from '../store/api/reportsApi'

// ─── Tokens — light academic palette (ICFGL UEL) ──────────────────────────
const BG_PRIMARY = '#FFFFFF'
const BG_SOFT = '#FAFBFD'
const BG_PINK_SOFT = brand.lightPink // #FFEBF2 — pink wash accent
const INK = brand.navy // #144E8C
const ACCENT_PINK = '#D9356F'
const SOLID_BLUE = INK
const TEXT_BODY = '#2A3548'
const TEXT_DIM = '#5E6B7E'
const HAIRLINE = 'rgba(20,78,140,0.12)'
const BLUE_TINT_04 = 'rgba(20,78,140,0.04)'
const BLUE_TINT_08 = 'rgba(20,78,140,0.08)'
const BLUE_TINT_10 = 'rgba(20,78,140,0.10)'
const BLUE_TINT_14 = 'rgba(20,78,140,0.14)'
const BLUE_TINT_18 = 'rgba(20,78,140,0.18)'
const PINK_TINT_04 = 'rgba(217,53,111,0.04)'
const PINK_TINT_10 = 'rgba(217,53,111,0.10)'
const PINK_TINT_14 = 'rgba(217,53,111,0.14)'
const PINK_TINT_18 = 'rgba(217,53,111,0.18)'
const CARD_SHADOW = '0 14px 30px rgba(20,78,140,0.045)'
const CARD_SHADOW_HOVER = '0 18px 36px rgba(20,78,140,0.07)'
const AWARD_GOLD = '#C99A2E'
const AWARD_SILVER = '#8E98A8'
const AWARD_BRONZE = '#A56B45'

const overviewAccents = [
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: BLUE_TINT_08, iconColor: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: PINK_TINT_10, iconColor: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: BLUE_TINT_08, iconColor: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, iconBg: PINK_TINT_10, iconColor: ACCENT_PINK, accent: ACCENT_PINK },
]

const timelineAccents = [
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: ACCENT_PINK, accent: ACCENT_PINK },
]

const scheduleAccents = [
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: ACCENT_PINK, accent: ACCENT_PINK },
  { surface: BG_PRIMARY, border: HAIRLINE, marker: SOLID_BLUE, accent: SOLID_BLUE },
]

const awardAccents = [
  { surface: 'rgba(201,154,46,0.06)', border: 'rgba(201,154,46,0.2)', icon: AWARD_GOLD, shadow: 'rgba(201,154,46,0.14)' },
  { surface: 'rgba(142,152,168,0.07)', border: 'rgba(142,152,168,0.22)', icon: AWARD_SILVER, shadow: 'rgba(142,152,168,0.12)' },
  { surface: 'rgba(165,107,69,0.08)', border: 'rgba(165,107,69,0.22)', icon: AWARD_BRONZE, shadow: 'rgba(165,107,69,0.12)' },
]

function normalizeSearchText(value: string) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/đ/g, 'd')
    .trim()
}

function getPreviewExcerpt(value: string, maxChars = 118) {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim()
  if (!normalized) {
    return { preview: '', truncated: false }
  }

  if (normalized.length <= maxChars) {
    return { preview: normalized, truncated: false }
  }

  const clipped = normalized.slice(0, maxChars)
  const safePreview = clipped.includes(' ') ? clipped.slice(0, clipped.lastIndexOf(' ')) : clipped

  return {
    preview: `${safePreview.trim()}...`,
    truncated: true,
  }
}

function getDisclosureButtonSx(
  active: boolean,
  accentColor: string,
  surfaceColor: string,
  borderColor: string,
) {
  return {
    alignSelf: 'flex-start',
    minHeight: 42,
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    px: 2,
    color: active ? 'common.white' : accentColor,
    border: `1px solid ${borderColor}`,
    borderColor,
    backgroundColor: active ? accentColor : BG_PRIMARY,
    boxShadow: 'none',
    '&:hover': {
      borderColor,
      backgroundColor: active ? accentColor : surfaceColor,
      boxShadow: 'none',
    },
  }
}

// NAV_LINKS được khởi tạo INSIDE TopBar component để dùng useTranslation hook.

// Eyebrow tag — navy uppercase letter-spaced (ICFGL academic feel)
function Eyebrow({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: '0.76rem',
        letterSpacing: 3,
        color: dark ? 'rgba(255,255,255,0.78)' : brand.hotPink,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  )
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'left',
}: {
  eyebrow: string
  title: string
  subtitle?: string
  align?: 'left' | 'center'
}) {
  return (
    <Stack
      spacing={2}
      alignItems={align === 'center' ? 'center' : 'flex-start'}
      textAlign={align === 'center' ? 'center' : 'left'}
      mb={{ xs: 3.5, md: 4.5 }}
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <Typography
        component="h2"
        sx={{
          color: INK,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: -0.5,
          fontSize: { xs: '1.75rem', md: '2.4rem' },
          lineHeight: 1.12,
        }}
      >
        {title}
      </Typography>
      {/* Horizontal divider rule — ICFGL marker */}
      <Box
        sx={{
          width: 64,
          height: 3,
          background: gradientBrand,
          borderRadius: 1,
          alignSelf: align === 'center' ? 'center' : 'flex-start',
        }}
      />
      {subtitle && (
        <Typography sx={{ color: TEXT_DIM, maxWidth: 760, fontSize: '1rem', lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      )}
    </Stack>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Top Nav — white/blur khi scroll, transparent khi top hero
// ═══════════════════════════════════════════════════════════════════════
function TopBar({ isAuthenticated, scrolled }: { isAuthenticated: boolean; scrolled: boolean }) {
  const theme = useTheme()
  // Breakpoint `lg` (1200px) — desktop có thể vừa 7 link + switcher + login button;
  // dưới ngưỡng này dùng hamburger menu để tránh wrap.
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  // Trimmed nav (7 link essential) — các section còn lại (Schedule, Results,
  // Organizers, Teams, Partners) vẫn render trên trang, user scroll xuống xem.
  const NAV_LINKS = [
    { label: t('nav.home'), href: '#hero' },
    { label: t('nav.about'), href: '#overview' },
    { label: t('nav.timeline'), href: '#timeline' },
    { label: t('nav.rules'), href: '#rules' },
    { label: t('nav.awards'), href: '#awards' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ]

  const textOnHero = !scrolled

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          borderBottom: scrolled ? `1px solid ${HAIRLINE}` : '1px solid transparent',
          color: scrolled ? INK : 'common.white',
          transition: 'all 0.25s ease',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: '100%', mx: 'auto', minHeight: { xs: 60, md: 72 }, gap: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ minWidth: 0, flexShrink: 1 }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Phiên Toà Giả Định - Công Lý Giới"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                flexShrink: 0,
                filter: scrolled
                  ? 'drop-shadow(0 2px 6px rgba(20,78,140,0.2))'
                  : 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  letterSpacing: 0.5,
                  lineHeight: 1.1,
                  color: textOnHero ? 'common.white' : INK,
                }}
              >
                {t('brand.short')}
              </Typography>
              <Typography
                noWrap
                sx={{
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  color: brand.hotPink,
                  fontSize: '0.66rem',
                  letterSpacing: 2,
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                {t('brand.season')}
              </Typography>
            </Box>
          </Stack>

          {!isMobile ? (
            <Stack
              direction="row"
              spacing={0.25}
              sx={{ flexGrow: 1, justifyContent: 'center' }}
            >
              {NAV_LINKS.map((link) => (
                <Button
                  key={link.href}
                  href={link.href}
                  sx={{
                    color: textOnHero ? 'rgba(255,255,255,0.85)' : TEXT_BODY,
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    textTransform: 'none',
                    px: 1.1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: textOnHero ? 'common.white' : INK,
                      background: textOnHero ? 'rgba(255,255,255,0.08)' : 'rgba(20,78,140,0.06)',
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => setOpen((v) => !v)}
                sx={{ color: textOnHero ? 'common.white' : INK }}
              >
                {open ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          )}

          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <LanguageSwitcher dark={textOnHero} />
          </Box>

          <Button
            component={RouterLink}
            to={isAuthenticated ? '/dashboard' : '/login'}
            variant="contained"
            size="small"
            startIcon={<Login />}
            sx={{
              background: brand.hotPink,
              color: 'common.white',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              boxShadow: '0 6px 18px rgba(246,65,131,0.3)',
              '&:hover': { background: '#d9356f' },
            }}
          >
            {isAuthenticated ? t('topbar.dashboard') : t('topbar.login')}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Mobile drop-down */}
      {isMobile && open && (
        <Box
          sx={{
            position: 'fixed',
            top: 60,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${HAIRLINE}`,
            py: 2,
          }}
        >
          <Stack>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                sx={{
                  color: TEXT_BODY,
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 1.2,
                  borderRadius: 0,
                  '&:hover': { color: INK, background: 'rgba(20,78,140,0.05)' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Hero — gradientBackdrop (navy → pink → white) đúng ICFGL feel
// ═══════════════════════════════════════════════════════════════════════
function HeroSection({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { t } = useTranslation()

  return (
    <Box
      id="hero"
      sx={{
        position: 'relative',
        background: gradientBackdrop, // navy → light blue → light pink → white
        color: 'common.white',
        pt: { xs: 14, md: 18 },
        pb: { xs: 12, md: 16 },
        overflow: 'hidden',
      }}
    >
      {/* Soft pink halo top-right — feminist accent */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 540,
          height: 540,
          top: -160,
          right: -160,
          background: 'radial-gradient(circle, rgba(246,65,131,0.22) 0%, transparent 65%)',
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Stack
          spacing={{ xs: 3, md: 3.5 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          textAlign={{ xs: 'left', md: 'center' }}
        >
          <Eyebrow dark>{t('hero.eyebrow')}</Eyebrow>

          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: -1,
              fontSize: { xs: '2.3rem', sm: '3.2rem', md: '4.2rem' },
              maxWidth: 980,
              color: 'common.white',
              textShadow: '0 4px 24px rgba(0,0,0,0.18)',
            }}
          >
            {t('hero.title')}
          </Typography>

          <Typography
            sx={{
              color: 'common.white',
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              letterSpacing: 4,
              fontWeight: 700,
              opacity: 0.92,
            }}
          >
            {t('hero.subtitle')}
          </Typography>

          <Box
            sx={{
              width: 80,
              height: 3,
              background: brand.hotPink,
              borderRadius: 1,
              alignSelf: { xs: 'flex-start', md: 'center' },
            }}
          />

          <Typography
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 720,
              fontSize: { xs: '1rem', md: '1.12rem' },
              lineHeight: 1.7,
            }}
          >
            {t('hero.tagline')}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
            {/* 2026-05-25: nút "Đăng ký ngay" mới — link đến Google Form
                đăng ký email đội trưởng. BTC nhận form → cấp tài khoản thủ
                công. URL trong [[siteContent.registrationFormUrl]] —
                placeholder rỗng cho đến khi legal team gửi link.
                Chỉ hiện cho khách chưa đăng nhập (authenticated user đã có
                account → không cần re-register). */}
            {!isAuthenticated && (
              <Tooltip title="Mở form đăng ký Google ở tab mới">
                <Button
                  component="a"
                  href={
                    siteContent.registrationFormUrl ||
                    'https://docs.google.com/forms/d/e/1FAIpQLSe7AXGzOp0rrxkAx1ipx3_fq0M0IFRj_i1BSClaQomLHPwlow/viewform'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  startIcon={<HowToReg />}
                  endIcon={<OpenInNew fontSize="small" />}
                  sx={{
                    background: '#d9356f',
                    color: 'common.white',
                    fontWeight: 800,
                    px: 4.5,
                    py: 1.5,
                    fontSize: '1rem',
                    letterSpacing: 1,
                    textTransform: 'none',
                    boxShadow: '0 14px 36px rgba(217,53,111,0.55)',
                    '&:hover': {
                      background: '#b82a5d',
                      boxShadow: '0 16px 40px rgba(184,42,93,0.65)',
                    },
                  }}
                >
                  {t('hero.ctaRegister')}
                </Button>
              </Tooltip>
            )}
            <Button
              component={RouterLink}
              to={isAuthenticated ? '/dashboard' : '/login'}
              variant={isAuthenticated ? 'contained' : 'outlined'}
              size="large"
              startIcon={<Login />}
              sx={
                isAuthenticated
                  ? {
                      background: '#d9356f',
                      color: 'common.white',
                      fontWeight: 800,
                      px: 4.5,
                      py: 1.5,
                      fontSize: '1rem',
                      letterSpacing: 1,
                      textTransform: 'none',
                      boxShadow: '0 14px 36px rgba(217,53,111,0.55)',
                      '&:hover': {
                        background: '#b82a5d',
                        boxShadow: '0 16px 40px rgba(184,42,93,0.65)',
                      },
                    }
                  : {
                      color: 'common.white',
                      borderColor: 'common.white',
                      borderWidth: 2.5,
                      fontWeight: 800,
                      px: 4.5,
                      py: 1.5,
                      fontSize: '1rem',
                      letterSpacing: 1,
                      textTransform: 'none',
                      backgroundColor: 'rgba(0,0,0,0.18)',
                      '&:hover': {
                        borderColor: 'common.white',
                        borderWidth: 2.5,
                        backgroundColor: 'rgba(255,255,255,0.16)',
                      },
                    }
              }
            >
              {isAuthenticated ? t('topbar.dashboard') : t('hero.ctaPrimary')}
            </Button>
            <Button
              href="#rules"
              variant="contained"
              size="large"
              endIcon={<ArrowDownward />}
              sx={{
                color: 'common.white',
                background: 'rgba(20, 78, 140, 0.55)',
                border: '2px solid rgba(255,255,255,0.85)',
                fontWeight: 800,
                px: 4.5,
                py: 1.5,
                fontSize: '1rem',
                letterSpacing: 1,
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                '&:hover': {
                  background: 'rgba(20, 78, 140, 0.75)',
                  borderColor: 'common.white',
                  boxShadow: '0 10px 28px rgba(0,0,0,0.25)',
                },
              }}
            >
              {t('hero.ctaSecondary')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Stats — white bg với số gradient brand
// ═══════════════════════════════════════════════════════════════════════
function StatsSection() {
  const { t } = useTranslation()
  // Stats values là metric cứng — giữ nguyên từ siteContent. Label dịch qua i18n.
  const statLabelKeys = ['stats.teams', 'stats.universities', 'stats.judges', 'stats.rounds'] as const
  const stats = siteContent.stats.map((s, idx) => ({
    value: s.value,
    label: t(statLabelKeys[idx] ?? 'stats.teams'),
  }))

  return (
    <Box sx={{ background: BG_SOFT, borderBottom: `1px solid ${HAIRLINE}`, py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 0 }}>
          {stats.map((s, idx) => (
            <Grid
              item
              xs={6}
              md={3}
              key={statLabelKeys[idx] ?? `stat-${idx}`}
              sx={{
                textAlign: 'center',
                borderRight: {
                  md: idx < stats.length - 1 ? `1px solid ${HAIRLINE}` : 'none',
                },
                py: { xs: 2, md: 3 },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  letterSpacing: -1,
                  background: gradientBrand,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.05,
                }}
              >
                {s.value}
              </Typography>
              <Typography
                sx={{
                  color: TEXT_DIM,
                  fontSize: '0.78rem',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  mt: 1,
                }}
              >
                {s.label}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Overview — white bg
// ═══════════════════════════════════════════════════════════════════════
function OverviewSection() {
  const { t } = useTranslation()
  const icons = [<Gavel />, <AccountBalance />, <Diversity3 />, <EmojiEvents />]
  const highlights = t('overview.highlights', { returnObjects: true }) as Array<{
    title: string
    description: string
  }>
  const slogan = t('overview.slogan')
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  return (
    <Box id="overview" sx={{ py: { xs: 7, md: 10 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('overview.eyebrow')}
          title={t('overview.title')}
          subtitle={t('overview.description')}
        />

        <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="flex-start">
          {highlights.map((item, index) => {
            const accent = overviewAccents[index % overviewAccents.length]
            const excerpt = getPreviewExcerpt(item.description)
            const isExpanded = expandedIndex === index

            return (
              <Grid item xs={12} sm={6} md={3} key={item.title} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 3,
                    background: accent.surface,
                    border: `1px solid ${accent.border}`,
                    borderTop: `2px solid ${accent.accent}`,
                    boxShadow: CARD_SHADOW,
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      borderColor: accent.accent,
                      boxShadow: CARD_SHADOW_HOVER,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      background: accent.iconBg,
                      color: accent.iconColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {icons[index % icons.length]}
                  </Box>
                  <Typography
                    sx={{
                      color: INK,
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      lineHeight: 1.35,
                      minHeight: { xs: 'auto', md: 56 },
                      mb: 1.25,
                    }}
                  >
                    {item.title}
                  </Typography>
                  {!isExpanded ? (
                    <Typography
                      sx={{
                        color: TEXT_DIM,
                        fontSize: '0.92rem',
                        lineHeight: 1.72,
                        minHeight: { xs: 'auto', md: 74 },
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 3,
                        overflow: 'hidden',
                      }}
                    >
                      {excerpt.preview}
                    </Typography>
                  ) : null}
                  <Button
                    variant={isExpanded ? 'contained' : 'outlined'}
                    onClick={() => setExpandedIndex((prev) => (prev === index ? null : index))}
                    aria-expanded={isExpanded}
                    aria-controls={`overview-key-information-${index}`}
                    endIcon={
                      <ExpandMore
                        sx={{
                          transition: 'transform 0.25s ease',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    }
                    sx={{
                      ...getDisclosureButtonSx(isExpanded, accent.accent, BLUE_TINT_04, accent.border),
                      mt: isExpanded ? 0.5 : 1.5,
                      minWidth: 176,
                    }}
                  >
                    {t('common.keyInformation')}
                  </Button>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateRows: isExpanded ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.3s ease',
                    }}
                  >
                    <Box id={`overview-key-information-${index}`} sx={{ overflow: 'hidden' }}>
                      <Box sx={{ pt: isExpanded ? 1.1 : 0 }}>
                        <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem', lineHeight: 1.7 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        <Typography
          sx={{
            mt: { xs: 2.5, md: 3.5 },
            color: INK,
            fontStyle: 'italic',
            textAlign: 'center',
            fontSize: { xs: '1.18rem', md: '1.48rem' },
            fontWeight: 700,
            lineHeight: 1.75,
            letterSpacing: -0.2,
          }}
        >
          {slogan}
        </Typography>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Timeline — off-white section, 3 phase cards
// ═══════════════════════════════════════════════════════════════════════
function TimelineSection() {
  const { t } = useTranslation()
  const phases = t('timeline.phases', { returnObjects: true }) as Array<{
    code: string
    title: string
    date: string
    description: string
    description2?: string
    status?: 'opening' | 'comingSoon' | 'completed'
  }>
  const statusLabels = t('timeline.statusLabels', { returnObjects: true }) as Record<string, string>
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const getStatusSx = (status: string) => {
    switch (status) {
      case 'opening':
        return {
          color: ACCENT_PINK,
          background: PINK_TINT_10,
          border: `1px solid ${PINK_TINT_18}`,
        }
      case 'completed':
        return {
          color: INK,
          background: BLUE_TINT_10,
          border: `1px solid ${BLUE_TINT_18}`,
        }
      case 'comingSoon':
      default:
        return {
          color: INK,
          background: BLUE_TINT_04,
          border: `1px solid ${BLUE_TINT_18}`,
        }
    }
  }

  return (
    <Box id="timeline" sx={{ py: { xs: 7, md: 10 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('timeline.eyebrow')}
          title={t('timeline.title')}
          subtitle={t('timeline.subtitle')}
        />

        <Box sx={{ position: 'relative' }}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: { xs: 13, md: '12.5%' },
              right: { xs: 'auto', md: '12.5%' },
              top: 14,
              bottom: { xs: 14, md: 'auto' },
              width: { xs: 2, md: 'auto' },
              height: { xs: 'calc(100% - 28px)', md: 2 },
              background: BLUE_TINT_18,
              borderRadius: 999,
            }}
          />

          <Grid container spacing={{ xs: 3, md: 2.5 }} alignItems="flex-start" sx={{ position: 'relative' }}>
            {phases.map((p, idx) => {
              const statusKey = p.status || 'comingSoon'
              const statusLabel = statusLabels[statusKey] || statusKey
              const accent = timelineAccents[idx % timelineAccents.length]

              return (
                <Grid item xs={12} md={3} key={p.code} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: { xs: 'row', md: 'column' },
                      gap: { xs: 2, md: 2 },
                      alignItems: { xs: 'stretch', md: 'center' },
                    }}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{
                        width: { xs: 28, md: '100%' },
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: accent.marker,
                          color: 'common.white',
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontWeight: 900,
                          fontSize: '0.82rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 18px rgba(20,78,140,0.22)',
                          zIndex: 1,
                        }}
                      >
                        {idx + 1}
                      </Box>
                    </Stack>

                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'flex-start',
                        p: { xs: 2.25, md: 2.75 },
                        background: accent.surface,
                        border: `1px solid ${accent.border}`,
                        borderTop: `2px solid ${accent.accent}`,
                        borderRadius: 3,
                        boxShadow: CARD_SHADOW,
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          borderColor: accent.accent,
                          boxShadow: CARD_SHADOW_HOVER,
                        },
                      }}
                    >
                      <Stack spacing={0} sx={{ width: '100%' }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1.5}
                          sx={{ minHeight: { xs: 'auto', md: 124 }, mb: 1.25 }}
                        >
                          <Box sx={{ minWidth: 0, pr: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontWeight: 900,
                                fontSize: { xs: '1.2rem', md: '1.45rem' },
                                letterSpacing: -0.5,
                                color: accent.accent,
                              }}
                            >
                              {p.code}
                            </Typography>
                            <Typography
                              sx={{
                                color: INK,
                                fontWeight: 800,
                                fontSize: '1rem',
                                lineHeight: 1.35,
                                mt: 0.5,
                              }}
                            >
                              {p.title}
                            </Typography>
                            <Typography
                              sx={{
                                color: accent.accent,
                                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontSize: '0.8rem',
                                letterSpacing: 0.8,
                                fontWeight: 700,
                                mt: 1,
                                lineHeight: 1.5,
                              }}
                            >
                              {p.date}
                            </Typography>
                          </Box>

                          <Chip label={statusLabel} size="small" sx={{ fontWeight: 700, ...getStatusSx(statusKey) }} />
                        </Stack>

                        <Button
                          variant={expandedIndex === idx ? 'contained' : 'outlined'}
                          onClick={() => setExpandedIndex((prev) => (prev === idx ? null : idx))}
                          aria-expanded={expandedIndex === idx}
                          aria-controls={`timeline-key-information-${idx}`}
                          endIcon={
                            <ExpandMore
                              sx={{
                                transition: 'transform 0.25s ease',
                                transform: expandedIndex === idx ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}
                            />
                          }
                          sx={{
                            ...getDisclosureButtonSx(expandedIndex === idx, accent.accent, BLUE_TINT_04, accent.border),
                            mt: 0,
                            minWidth: 176,
                          }}
                        >
                          {t('common.keyInformation')}
                        </Button>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateRows: expandedIndex === idx ? '1fr' : '0fr',
                            transition: 'grid-template-rows 0.3s ease',
                          }}
                        >
                          <Box id={`timeline-key-information-${idx}`} sx={{ overflow: 'hidden' }}>
                            <Box sx={{ pt: expandedIndex === idx ? 1.35 : 0 }}>
                              <Typography sx={{ color: TEXT_BODY, fontSize: '0.94rem', lineHeight: 1.75 }}>
                                {p.description}
                              </Typography>
                              {p.description2 ? (
                                <Typography sx={{ color: TEXT_BODY, fontSize: '0.94rem', lineHeight: 1.75, mt: 1.25 }}>
                                  {p.description2}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Competition Timeline — thay block Lịch các phiên thi
// ═══════════════════════════════════════════════════════════════════════
function getStatusSx(status: string) {
  switch (status) {
    case 'opening':
      return {
        color: ACCENT_PINK,
        background: PINK_TINT_10,
        border: `1px solid ${PINK_TINT_18}`,
      }
    case 'completed':
      return {
        color: INK,
        background: BLUE_TINT_10,
        border: `1px solid ${BLUE_TINT_18}`,
      }
    case 'comingSoon':
    default:
      return {
        color: INK,
        background: BLUE_TINT_04,
        border: `1px solid ${BLUE_TINT_18}`,
      }
  }
}

function ScheduleSection() {
  const { t } = useTranslation()
  const items = t('competitionTimeline.items', { returnObjects: true }) as Array<{
    title: string
    date: string
    details?: string[]
    status?: 'opening' | 'comingSoon' | 'completed'
  }>
  const statusLabels = t('competitionTimeline.statusLabels', {
    returnObjects: true,
  }) as Record<string, string>
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  return (
    <Box
      id="schedule"
      sx={{
        pt: { xs: 7, md: 9 },
        pb: { xs: 4.5, md: 6 },
        background: BG_SOFT,
      }}
    >
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('competitionTimeline.eyebrow')}
          title={t('competitionTimeline.title')}
          subtitle={t('competitionTimeline.subtitle')}
        />

        <Box sx={{ position: 'relative' }}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: { xs: 13, md: '10%' },
              right: { xs: 'auto', md: '10%' },
              top: 14,
              bottom: { xs: 14, md: 'auto' },
              width: { xs: 2, md: 'auto' },
              height: { xs: 'calc(100% - 28px)', md: 2 },
              background: BLUE_TINT_18,
              borderRadius: 999,
            }}
          />

          <Grid container spacing={{ xs: 3, md: 2.5 }} alignItems="flex-start" sx={{ position: 'relative' }}>
            {items.map((item, idx) => {
              const statusKey = item.status || 'comingSoon'
              const statusLabel = statusLabels[statusKey] || statusKey
              const hasDetails = Boolean(item.details?.length)
              const accent = scheduleAccents[idx % scheduleAccents.length]
              const isExpanded = expandedIndex === idx

              return (
                <Grid
                  item
                  xs={12}
                  md={items.length === 5 ? 4 : 3}
                  lg={items.length === 5 ? 12 / 5 : 3}
                  key={`${item.title}_${idx}`}
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: { xs: 'row', md: 'column' },
                      gap: { xs: 2, md: 2 },
                      alignItems: { xs: 'stretch', md: 'center' },
                    }}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{ width: { xs: 28, md: '100%' }, flexShrink: 0 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: accent.marker,
                          color: 'common.white',
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontWeight: 900,
                          fontSize: '0.82rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}
                      >
                        {idx + 1}
                      </Box>
                    </Stack>

                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'flex-start',
                        p: { xs: 2.25, md: 2.75 },
                        background: accent.surface,
                        border: `1px solid ${accent.border}`,
                        borderTop: `2px solid ${accent.accent}`,
                        borderRadius: 3,
                        boxShadow: CARD_SHADOW,
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          borderColor: accent.accent,
                          boxShadow: CARD_SHADOW_HOVER,
                        },
                      }}
                    >
                      <Stack spacing={0} sx={{ width: '100%' }}>
                        <Stack
                          spacing={1}
                          sx={{
                            minHeight: { xs: 'auto', lg: 118 },
                            mb: 0.9,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                            <Typography
                              sx={{
                                color: INK,
                                fontWeight: 800,
                                fontSize: '1rem',
                                lineHeight: 1.35,
                                pr: 1,
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{ fontWeight: 700, flexShrink: 0, ...getStatusSx(statusKey) }}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Schedule sx={{ color: accent.accent, fontSize: 18 }} />
                            <Typography
                              sx={{
                                color: TEXT_DIM,
                                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontSize: '0.8rem',
                                letterSpacing: 0.3,
                                fontWeight: 700,
                                lineHeight: 1.5,
                              }}
                            >
                              {item.date}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Box
                          sx={{
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                          }}
                        >
                          {hasDetails ? (
                            <IconButton
                              onClick={() => setExpandedIndex((prev) => (prev === idx ? null : idx))}
                              aria-expanded={isExpanded}
                              aria-controls={`competition-timeline-key-information-${idx}`}
                              aria-label={t('common.keyInformation')}
                              size="small"
                              sx={{
                                width: 28,
                                height: 28,
                                p: 0,
                                color: isExpanded ? 'common.white' : accent.accent,
                                backgroundColor: isExpanded ? accent.accent : BG_PRIMARY,
                                border: `1px solid ${isExpanded ? accent.accent : accent.border}`,
                                '&:hover': {
                                  backgroundColor: isExpanded ? accent.accent : BLUE_TINT_04,
                                  borderColor: accent.accent,
                                },
                              }}
                            >
                              <ExpandMore
                                sx={{
                                  fontSize: 20,
                                  transition: 'transform 0.25s ease',
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                              />
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 28, height: 28, flexShrink: 0 }} aria-hidden />
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateRows: hasDetails && isExpanded ? '1fr' : '0fr',
                            transition: 'grid-template-rows 0.3s ease',
                          }}
                        >
                          <Box
                            id={`competition-timeline-key-information-${idx}`}
                            sx={{ overflow: 'hidden' }}
                          >
                            <Stack spacing={1.1} sx={{ pt: hasDetails && isExpanded ? 1.05 : 0 }}>
                              {item.details?.map((detail) => (
                                <Stack
                                  key={detail}
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                >
                                  <CheckCircle
                                    sx={{
                                      color: accent.accent,
                                      fontSize: 18,
                                      mt: 0.25,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      color: TEXT_BODY,
                                      fontSize: '0.92rem',
                                      lineHeight: 1.7,
                                    }}
                                  >
                                    {detail}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Competition Highlights & Rankings
// ═══════════════════════════════════════════════════════════════════════
function getCompetitionStatusLabel(
  roundType: string | null | undefined,
  competitionStatus: string | null | undefined,
  t: (key: string) => string,
) {
  if (roundType === 'final' || competitionStatus === 'advanced_to_final') {
    return t('highlightsSection.status.finalist')
  }
  return t('highlightsSection.status.qualified')
}

function ResultsCaseCard({ caseId, title }: { caseId: string; title: string }) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isCompact = useMediaQuery(theme.breakpoints.down('md'))
  const { data, isLoading } = useGetPublicLatestResultsQuery(caseId, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: BG_PRIMARY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    )
  }

  if (!data) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          background: BG_PRIMARY,
          border: `1px dashed ${HAIRLINE}`,
          borderRadius: 2,
        }}
      >
        <Typography sx={{ color: INK, fontWeight: 800, mb: 0.75 }}>{title}</Typography>
        <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem', lineHeight: 1.65 }}>
          {t('highlightsSection.pending')}
        </Typography>
      </Paper>
    )
  }

  const publishedDate = data.published_at
    ? new Date(data.published_at).toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US')
    : '—'
  const rows = data.advancing_teams.slice(0, 4)

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        background: BG_PRIMARY,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 14px 32px rgba(20,78,140,0.06)',
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Box>
            <Typography
              sx={{
                color: TEXT_DIM,
                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                fontSize: '0.76rem',
                letterSpacing: 1.4,
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              {data.round?.round_name || t('highlightsSection.latestRound')}
            </Typography>
            <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.08rem' }}>
              {data.case_title || title}
            </Typography>
          </Box>
          <Chip
            label={`${t('highlightsSection.publishedLabel')} ${publishedDate}`}
            size="small"
            sx={{
              fontWeight: 700,
              color: SOLID_BLUE,
              background: 'rgba(20,78,140,0.08)',
              border: `1px solid rgba(20,78,140,0.14)`,
            }}
          />
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                background: BG_SOFT,
                border: `1px solid ${HAIRLINE}`,
              }}
            >
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem', mb: 0.75 }}>
                {t('highlightsSection.metrics.totalTeams')}
              </Typography>
              <Typography
                sx={{
                  color: INK,
                  fontWeight: 900,
                  fontSize: '1.65rem',
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  lineHeight: 1,
                }}
              >
                {data.total_teams}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'rgba(246,65,131,0.08)',
                border: `1px solid ${HAIRLINE}`,
              }}
            >
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem', mb: 0.75 }}>
                {t('highlightsSection.metrics.advancingTeams')}
              </Typography>
              <Typography
                sx={{
                  color: brand.hotPink,
                  fontWeight: 900,
                  fontSize: '1.65rem',
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  lineHeight: 1,
                }}
              >
                {data.advancing_count}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${HAIRLINE}`,
            overflow: 'hidden',
          }}
        >
          {!isCompact ? (
            <Grid
              container
              sx={{
                px: 2,
                py: 1.25,
                background: BG_SOFT,
                borderBottom: `1px solid ${HAIRLINE}`,
              }}
            >
              <Grid item xs={2}>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.76rem', fontWeight: 700 }}>
                  {t('highlightsSection.columns.rank')}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.76rem', fontWeight: 700 }}>
                  {t('highlightsSection.columns.team')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.76rem', fontWeight: 700 }}>
                  {t('highlightsSection.columns.university')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.76rem', fontWeight: 700 }}>
                  {t('highlightsSection.columns.status')}
                </Typography>
              </Grid>
            </Grid>
          ) : null}

          {rows.length === 0 ? (
            <Typography sx={{ color: TEXT_DIM, p: 2, fontSize: '0.92rem' }}>
              {t('highlightsSection.pending')}
            </Typography>
          ) : (
            rows.map((team, index) => (
              <Box
                key={`${team.team_name}_${index}`}
                sx={{
                  px: 2,
                  py: 1.5,
                  background: index === 0 ? 'rgba(255,235,242,0.56)' : BG_PRIMARY,
                  borderTop: index === 0 ? 'none' : `1px solid ${HAIRLINE}`,
                  borderLeft: index === 0 ? `4px solid ${ACCENT_PINK}` : '4px solid transparent',
                }}
              >
                {isCompact ? (
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                      <Chip
                        label={`${t('highlightsSection.columns.rank')} ${String(index + 1).padStart(2, '0')}`}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          color: index === 0 ? ACCENT_PINK : SOLID_BLUE,
                          background: index === 0 ? 'rgba(246,65,131,0.12)' : 'rgba(20,78,140,0.08)',
                          border: `1px solid ${index === 0 ? 'rgba(246,65,131,0.18)' : 'rgba(20,78,140,0.14)'}`,
                        }}
                      />
                      <Chip
                        label={getCompetitionStatusLabel(data.round?.round_type, team.competition_status, t)}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: index === 0 ? ACCENT_PINK : INK,
                          background: index === 0 ? 'rgba(246,65,131,0.12)' : 'rgba(20,78,140,0.08)',
                          border: `1px solid ${index === 0 ? 'rgba(246,65,131,0.18)' : 'rgba(20,78,140,0.14)'}`,
                        }}
                      />
                    </Stack>
                    <Typography sx={{ color: INK, fontWeight: 800, fontSize: '0.98rem' }}>
                      {team.team_name}
                    </Typography>
                    <Typography sx={{ color: TEXT_DIM, fontSize: '0.88rem', lineHeight: 1.5 }}>
                      {team.university || '—'}
                    </Typography>
                  </Stack>
                ) : (
                  <Grid container alignItems="center">
                    <Grid item xs={2}>
                      <Typography sx={{ color: INK, fontWeight: 800 }}>
                        {String(index + 1).padStart(2, '0')}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography sx={{ color: INK, fontWeight: 700, fontSize: '0.94rem' }}>
                        {team.team_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={{ color: TEXT_DIM, fontSize: '0.86rem', lineHeight: 1.5 }}>
                        {team.university || '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Chip
                        label={getCompetitionStatusLabel(data.round?.round_type, team.competition_status, t)}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: index === 0 ? ACCENT_PINK : INK,
                          background: index === 0 ? 'rgba(246,65,131,0.12)' : 'rgba(20,78,140,0.08)',
                          border: `1px solid ${index === 0 ? 'rgba(246,65,131,0.18)' : 'rgba(20,78,140,0.14)'}`,
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            ))
          )}
        </Box>
      </Stack>
    </Paper>
  )
}

function ResultsSection() {
  const { t } = useTranslation()
  const { data: cases = [], isLoading, isError } = useGetPublicResultsCasesQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const summaryPoints = t('highlightsSection.summaryPoints', { returnObjects: true }) as string[]
  const progressUpdates = t('highlightsSection.progressUpdates', { returnObjects: true }) as string[]
  const notices = t('highlightsSection.notices', { returnObjects: true }) as string[]

  return (
    <Box id="highlights" sx={{ py: { xs: 8, md: 12 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('highlightsSection.eyebrow')}
          title={t('highlightsSection.title')}
          subtitle={t('highlightsSection.subtitle')}
        />

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderTop: `2px solid ${SOLID_BLUE}`,
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem', mb: 1.75 }}>
                  {t('highlightsSection.summaryTitle')}
                </Typography>
                <Stack spacing={1.1}>
                  {summaryPoints.map((point) => (
                    <Stack key={point} direction="row" spacing={1} alignItems="flex-start">
                      <CheckCircle
                        sx={{ color: brand.hotPink, fontSize: 18, mt: 0.25, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: TEXT_BODY, fontSize: '0.94rem', lineHeight: 1.65 }}>
                        {point}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : isError || cases.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: BG_PRIMARY,
                    border: `1px dashed ${HAIRLINE}`,
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ color: INK, fontWeight: 700, mb: 1 }}>
                    {t('highlightsSection.empty')}
                  </Typography>
                  <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem' }}>
                    {t('highlightsSection.emptyBody')}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={2.5}>
                  {cases.map((c) => (
                    <ResultsCaseCard key={c.case_id} caseId={c.case_id} title={c.title} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderTop: `2px solid ${ACCENT_PINK}`,
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ color: ACCENT_PINK, fontWeight: 800, fontSize: '1.05rem', mb: 1.75 }}>
                  {t('highlightsSection.noticesTitle')}
                </Typography>
                <Stack spacing={1.1}>
                  {notices.map((notice, index) => (
                    <Paper
                      key={notice}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        background: index === 0 ? PINK_TINT_04 : BG_SOFT,
                        border: `1px solid ${index === 0 ? PINK_TINT_14 : HAIRLINE}`,
                      }}
                    >
                      <Typography sx={{ color: TEXT_BODY, fontSize: '0.9rem', lineHeight: 1.6 }}>
                        {notice}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderTop: `2px solid ${SOLID_BLUE}`,
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem', mb: 1.75 }}>
                  {t('highlightsSection.progressTitle')}
                </Typography>
                <Stack spacing={1.35}>
                  {progressUpdates.map((update) => (
                    <Stack key={update} direction="row" spacing={1} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            mt: 0.8,
                            borderRadius: '50%',
                            flexShrink: 0,
                            background: ACCENT_PINK,
                          }}
                        />
                      <Typography sx={{ color: TEXT_BODY, fontSize: '0.92rem', lineHeight: 1.7 }}>
                        {update}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Rules — white bg, 2-column cards
// ═══════════════════════════════════════════════════════════════════════
function RulesSection() {
  const { t } = useTranslation()
  const fileHref = t('rules.fileHref')

  return (
    <Box id="rules" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('rules.eyebrow')}
          title={t('rules.title')}
          subtitle={t('rules.subtitle')}
        />

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            background: BG_PRIMARY,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 3,
            boxShadow: CARD_SHADOW,
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} lg={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  height: '100%',
                  background: BG_SOFT,
                  border: `1px solid ${HAIRLINE}`,
                  borderTop: `2px solid ${SOLID_BLUE}`,
                  borderRadius: 2.5,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      background: BLUE_TINT_08,
                      color: SOLID_BLUE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Article sx={{ fontSize: 38 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.18rem', mb: 0.75 }}>
                      {t('rules.fileTitle')}
                    </Typography>
                    <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem', lineHeight: 1.75, mb: 1.5 }}>
                      {t('rules.fileDescription')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={t('rules.fileFormat')}
                        sx={{
                          fontWeight: 800,
                          color: SOLID_BLUE,
                          background: BG_SOFT,
                          border: '1px solid rgba(20,78,140,0.16)',
                        }}
                      />
                      <Chip
                        label={t('rules.fileTag')}
                        sx={{
                          fontWeight: 700,
                          color: ACCENT_PINK,
                          background: 'rgba(246,65,131,0.1)',
                          border: '1px solid rgba(246,65,131,0.16)',
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  height: '100%',
                  background: PINK_TINT_04,
                  border: `1px solid ${HAIRLINE}`,
                  borderTop: `2px solid ${ACCENT_PINK}`,
                  borderRadius: 2.5,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.06rem', mb: 0.75 }}>
                    {t('rules.title')}
                  </Typography>
                  <Typography sx={{ color: TEXT_BODY, fontSize: '0.92rem', lineHeight: 1.7 }}>
                    {t('rules.subtitle')}
                  </Typography>
                </Box>

                <Button
                  component="a"
                  href={fileHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  variant="contained"
                  endIcon={<OpenInNew fontSize="small" />}
                  sx={{
                    minHeight: 54,
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 999,
                    backgroundColor: SOLID_BLUE,
                    boxShadow: '0 14px 32px rgba(20,78,140,0.14)',
                    '&:hover': {
                      backgroundColor: SOLID_BLUE,
                      boxShadow: '0 16px 36px rgba(20,78,140,0.2)',
                    },
                  }}
                >
                  {t('rules.cta')}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Awards — light pink section (feminist accent)
// ═══════════════════════════════════════════════════════════════════════
function AwardsSection() {
  const { t } = useTranslation()
  const teamAwards = t('awards.teamAwards', { returnObjects: true }) as Array<{
    rank: string
    amount: string
    description?: string
  }>
  const summaryItems = t('awards.summary', { returnObjects: true }) as Array<{
    label: string
    value: string
  }>
  const specialAwards = t('awards.specialAwards', { returnObjects: true }) as Array<{
    title: string
    amount: string
    type: 'oral' | 'document'
  }>
  const opportunities = t('awards.opportunities', { returnObjects: true }) as string[]

  return (
    <Box id="awards" sx={{ py: { xs: 8, md: 12 }, background: BG_PINK_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('awards.eyebrow')}
          title={t('awards.title')}
          subtitle={t('awards.subtitle')}
        />

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3 },
                height: '100%',
                background: PINK_TINT_04,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${ACCENT_PINK}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem', mb: 2 }}>
                {t('awards.summaryTitle')}
              </Typography>
              <Stack spacing={1.5}>
                {summaryItems.map((item, index) => (
                  <Paper
                    key={item.label}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: index === 0 ? PINK_TINT_04 : BG_SOFT,
                      border: `1px solid ${HAIRLINE}`,
                    }}
                  >
                    <Typography sx={{ color: TEXT_DIM, fontSize: '0.82rem', mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: INK,
                        fontWeight: 900,
                        fontSize: '1.45rem',
                        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.15fr 1fr 1fr' },
                gap: 3,
              }}
            >
              {teamAwards.map((item, index) => {
                const accent = awardAccents[index % awardAccents.length]

                return (
                  <Paper
                    key={item.rank}
                    elevation={0}
                    sx={{
                      p: 3,
                      minHeight: index === 0 ? 300 : 270,
                      background: accent.surface,
                      border: `1px solid ${accent.border}`,
                      borderTop: `2px solid ${accent.icon}`,
                      borderRadius: 3,
                      textAlign: 'center',
                      boxShadow: index === 0 ? `0 12px 28px ${accent.shadow}` : 'none',
                    }}
                  >
                    <Box>
                      <EmojiEvents sx={{ fontSize: index === 0 ? 62 : 54, color: accent.icon, mb: 1.5 }} />
                      <Typography
                        sx={{
                          color: INK,
                          fontWeight: 800,
                          fontSize: index === 0 ? '1.3rem' : '1.15rem',
                          mb: 0.5,
                        }}
                      >
                        {item.rank}
                      </Typography>
                      <Typography
                        sx={{
                          color: accent.icon,
                          fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                          fontSize: '0.84rem',
                          letterSpacing: 1,
                          fontWeight: 800,
                          mb: 1.5,
                        }}
                      >
                        {item.amount}
                      </Typography>
                      {item.description ? (
                        <Typography sx={{ color: TEXT_BODY, fontSize: '0.92rem', lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </Paper>
                )
              })}
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.15rem', mb: 2 }}>
            {t('awards.specialAwardsTitle')}
          </Typography>
          <Grid container spacing={3}>
            {specialAwards.map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                    borderTop: `2px solid ${item.type === 'oral' ? SOLID_BLUE : ACCENT_PINK}`,
                    borderRadius: 3,
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      background: item.type === 'oral' ? BLUE_TINT_08 : PINK_TINT_10,
                      color: item.type === 'oral' ? INK : brand.hotPink,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {item.type === 'oral' ? <WorkspacePremium sx={{ fontSize: 32 }} /> : <Article sx={{ fontSize: 30 }} />}
                  </Box>
                  <Box>
                    <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem', mb: 0.75 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: TEXT_BODY, fontSize: '0.94rem', lineHeight: 1.7 }}>
                      {item.amount}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.15rem', mb: 2 }}>
            {t('awards.opportunitiesTitle')}
          </Typography>
          <Grid container spacing={3}>
            {opportunities.map((item, index) => (
              <Grid item xs={12} md={4} key={item}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: '100%',
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                    borderTop: `2px solid ${index % 2 === 0 ? SOLID_BLUE : ACCENT_PINK}`,
                    borderRadius: 3,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <CheckCircle sx={{ color: brand.hotPink, fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                    <Typography sx={{ color: TEXT_BODY, fontSize: '0.94rem', lineHeight: 1.7 }}>
                      {item}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, md: 5 },
            p: { xs: 3, md: 3.5 },
            background: BG_PRIMARY,
            border: `1px solid ${HAIRLINE}`,
            borderTop: `2px solid ${SOLID_BLUE}`,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.08rem', mb: 1 }}>
            {t('awards.certificateTitle')}
          </Typography>
          <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem', lineHeight: 1.75 }}>
            {t('awards.certificateBody')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Competition Moments
// ═══════════════════════════════════════════════════════════════════════
function PublicTeamsSection() {
  const { t } = useTranslation()
  const tags = t('moments.tags', { returnObjects: true }) as string[]

  return (
    <Box id="moments" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('moments.eyebrow')}
          title={t('moments.title')}
          subtitle={t('moments.subtitle')}
        />

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${SOLID_BLUE}`,
                background: BG_SOFT,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Typography
                sx={{
                  color: INK,
                  fontWeight: 800,
                  fontSize: { xs: '1.3rem', md: '1.7rem' },
                  lineHeight: 1.4,
                  mb: 2,
                }}
              >
                {t('moments.statement')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    sx={{
                      fontWeight: 700,
                      color: INK,
                      background: BG_PRIMARY,
                      border: `1px solid ${HAIRLINE}`,
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${ACCENT_PINK}`,
                background: PINK_TINT_04,
                boxShadow: CARD_SHADOW,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box>
                <Visibility sx={{ color: brand.hotPink, fontSize: 34, mb: 1.5 }} />
                <Typography
                  sx={{
                    color: INK,
                    fontStyle: 'italic',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.8,
                    mb: 1.5,
                  }}
                >
                  &ldquo;{t('moments.quote')}&rdquo;
                </Typography>
                <Typography
                  sx={{
                    color: TEXT_DIM,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    letterSpacing: 0.4,
                  }}
                >
                  {t('moments.author')}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Partners — white bg, logo placeholder tiles
// ═══════════════════════════════════════════════════════════════════════
function PartnersSection() {
  return null
}

// ═══════════════════════════════════════════════════════════════════════
// Contact — off-white section
// ═══════════════════════════════════════════════════════════════════════
function ContactSection() {
  const { t } = useTranslation()
  const registerHref =
    siteContent.registrationFormUrl ||
    'https://docs.google.com/forms/d/e/1FAIpQLSe7AXGzOp0rrxkAx1ipx3_fq0M0IFRj_i1BSClaQomLHPwlow/viewform'
  const channels = t('contact.channels', { returnObjects: true }) as Array<{
    label: string
    value: string
    href?: string
    kind: 'email' | 'fanpage' | 'website' | 'phone'
  }>
  const quickAccess = t('contact.quickAccess', { returnObjects: true }) as Array<{
    label: string
    action: 'register' | 'rules' | 'contact'
  }>
  const visibleChannels = channels.filter((channel) => channel.kind !== 'phone')
  const websiteHref = visibleChannels.find((channel) => channel.kind === 'website')?.href

  const getChannelIcon = (kind: string) => {
    switch (kind) {
      case 'fanpage':
        return <Facebook />
      case 'website':
        return <PublicIcon />
      case 'phone':
        return <Schedule />
      case 'email':
      default:
        return <Mail />
    }
  }

  const getChannelAccent = (kind: string) => {
    switch (kind) {
      case 'fanpage':
        return {
          solid: ACCENT_PINK,
          soft: PINK_TINT_04,
        }
      case 'email':
      case 'website':
      default:
        return {
          solid: SOLID_BLUE,
          soft: BLUE_TINT_04,
        }
    }
  }

  const getQuickAccessHref = (action: string) => {
    switch (action) {
      case 'register':
        return registerHref
      case 'rules':
        return '#rules'
      case 'contact':
      default:
        return visibleChannels.find((channel) => channel.kind === 'email')?.href || '#contact'
    }
  }

  return (
    <Box id="contact" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('contact.eyebrow')}
          title={t('contact.title')}
          subtitle={t('contact.subtitle')}
        />

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                background: BG_SOFT,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${SOLID_BLUE}`,
                borderRadius: 3,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.08rem', mb: 2.25 }}>
                {t('contact.informationTitle')}
              </Typography>

              <Stack spacing={1.5}>
                {visibleChannels.map((channel) => {
                  const channelSurface = channel.kind === 'fanpage' ? PINK_TINT_04 : BG_PRIMARY

                  return (
                    <Paper
                      key={channel.label}
                      elevation={0}
                      component={channel.kind === 'fanpage' || channel.kind === 'website' ? 'a' : 'div'}
                      href={channel.kind === 'fanpage' || channel.kind === 'website' ? channel.href : undefined}
                      target={channel.kind === 'fanpage' || channel.kind === 'website' ? '_blank' : undefined}
                      rel={channel.kind === 'fanpage' || channel.kind === 'website' ? 'noopener noreferrer' : undefined}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: channelSurface,
                        border: `1px solid ${HAIRLINE}`,
                        textDecoration: 'none',
                        transition: 'border-color 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                          borderColor:
                            channel.kind === 'fanpage' || channel.kind === 'website'
                              ? getChannelAccent(channel.kind).solid
                              : HAIRLINE,
                          background:
                            channel.kind === 'fanpage' || channel.kind === 'website'
                              ? getChannelAccent(channel.kind).soft
                              : channelSurface,
                        },
                      }}
                    >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: 1.5,
                          background: getChannelAccent(channel.kind).solid,
                          color: 'common.white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {getChannelIcon(channel.kind)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: INK,
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            textTransform: 'uppercase',
                            mb: 0.5,
                          }}
                        >
                          {channel.label}
                        </Typography>
                        {channel.kind === 'fanpage' || channel.kind === 'website' ? (
                          <OpenInNew sx={{ fontSize: 16, color: getChannelAccent(channel.kind).solid }} />
                        ) : channel.href ? (
                          <Typography
                            component="a"
                            href={channel.href}
                            target={channel.kind === 'email' || channel.kind === 'phone' ? undefined : '_blank'}
                            rel={channel.kind === 'email' || channel.kind === 'phone' ? undefined : 'noopener noreferrer'}
                            sx={{
                              color: TEXT_BODY,
                              fontSize: '0.95rem',
                              lineHeight: 1.65,
                              textDecoration: 'none',
                              wordBreak: 'break-word',
                              '&:hover': { color: brand.hotPink },
                            }}
                          >
                            {channel.value || '—'}
                          </Typography>
                        ) : (
                          <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem' }}>
                            {channel.value || '—'}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    </Paper>
                  )
                })}
              </Stack>

              <Stack direction="row" spacing={1.25} sx={{ mt: 2.5, flexWrap: 'wrap', rowGap: 1.25 }}>
                {channels
                  .filter((channel) => channel.href && channel.kind !== 'phone')
                  .map((channel) => (
                    <IconButton
                      key={channel.label}
                      component="a"
                      href={channel.href}
                      target={channel.kind === 'email' || channel.kind === 'phone' ? undefined : '_blank'}
                      rel={channel.kind === 'email' || channel.kind === 'phone' ? undefined : 'noopener noreferrer'}
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 1.5,
                        background: BLUE_TINT_04,
                        color: getChannelAccent(channel.kind).solid,
                        border: `1px solid ${HAIRLINE}`,
                        transition: 'border-color 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                          background: getChannelAccent(channel.kind).soft,
                          borderColor: getChannelAccent(channel.kind).solid,
                        },
                      }}
                    >
                      {getChannelIcon(channel.kind)}
                    </IconButton>
                  ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                width: '100%',
                height: '100%',
                background: PINK_TINT_04,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${ACCENT_PINK}`,
                borderRadius: 3,
                boxShadow: CARD_SHADOW,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 2.5,
              }}
            >
              <Box
                component={websiteHref ? 'a' : 'div'}
                href={websiteHref}
                target={websiteHref ? '_blank' : undefined}
                rel={websiteHref ? 'noopener noreferrer' : undefined}
                sx={{
                  mb: 0.5,
                  borderRadius: 2.5,
                  border: `1px solid ${HAIRLINE}`,
                  background: BLUE_TINT_04,
                  p: { xs: 3, md: 4 },
                  minHeight: { xs: 300, md: 360 },
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textDecoration: 'none',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  '&:hover': websiteHref
                    ? {
                        borderColor: SOLID_BLUE,
                        background: BG_PRIMARY,
                      }
                    : undefined,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 180, md: 220 },
                    height: { xs: 180, md: 220 },
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${BLUE_TINT_14}`,
                    background: BG_PRIMARY,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <QrCode2 sx={{ color: INK, fontSize: { xs: 128, md: 156 } }} />
                </Box>
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.02rem', mb: 0.75 }}>
                  {t('contact.quickAccessTitle')}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                {quickAccess.map((item, index) => (
                  <Button
                    key={item.label}
                    component="a"
                    href={getQuickAccessHref(item.action)}
                    target={item.action === 'register' ? '_blank' : undefined}
                    rel={item.action === 'register' ? 'noopener noreferrer' : undefined}
                    variant={index === 0 ? 'contained' : 'outlined'}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 52,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: { xs: '0.82rem', md: '0.94rem' },
                      px: { xs: 1, md: 1.5 },
                      whiteSpace: 'nowrap',
                      borderRadius: 999,
                      backgroundColor: index === 0 ? ACCENT_PINK : BLUE_TINT_04,
                      color: index === 0 ? 'common.white' : SOLID_BLUE,
                      borderColor: index === 0 ? ACCENT_PINK : SOLID_BLUE,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: index === 0 ? ACCENT_PINK : 'rgba(20,78,140,0.05)',
                        borderColor: index === 0 ? ACCENT_PINK : SOLID_BLUE,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 3, md: 3.5 },
            background: BG_SOFT,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 3,
            boxShadow: CARD_SHADOW,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem', mb: 1 }}>
            {t('contact.stillHaveQuestions')}
          </Typography>
          <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem' }}>
            {t('contact.stillHaveQuestionsBody')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Footer — navy bg anchor (reverse colors)
// ═══════════════════════════════════════════════════════════════════════
function FooterSection() {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        py: { xs: 4, md: 5 },
        background: INK,
        color: 'common.white',
        borderTop: `1px solid ${PINK_TINT_18}`,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={{ xs: 3, lg: 4 }}
          alignItems={{ xs: 'center', lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                component="img"
                src="/UEL_Logo final-02.png"
                alt="UEL"
                sx={{ width: 130, height: 130, objectFit: 'contain' }}
              />
              <Box
                component="img"
                src="/RLS_logo.png"
                alt="RLS"
                sx={{ width: 90, height: 90, objectFit: 'contain' }}
              />
            </Stack>
            <Box>
              <Typography sx={{ color: 'common.white', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('brand.short')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.8rem', lineHeight: 1.6 }}>
                {t('footer.organizer')}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={0.75} alignItems={{ xs: 'center', lg: 'flex-end' }} textAlign={{ xs: 'center', lg: 'right' }}>
            <Box
              sx={{
                width: { xs: 84, lg: 140 },
                height: 1,
                backgroundColor: 'rgba(255,255,255,0.18)',
              }}
            />
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.62)',
                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                fontSize: '0.78rem',
                letterSpacing: 0.8,
                lineHeight: 1.6,
              }}
            >
              © {t('footer.copyright')}
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Organizing Section — Ban Tổ chức & Hội đồng giám khảo
// ═══════════════════════════════════════════════════════════════════════
function OrganizingSection() {
  const { t } = useTranslation()
  const roles = t('organizing.committee.roles', { returnObjects: true }) as string[]
  const rounds = t('organizing.judges.rounds', { returnObjects: true }) as Array<{
    name: string
    body: string
  }>

  return (
    <Box
      id="organizers"
      sx={{
        py: { xs: 7, sm: 10 },
        background: BG_SOFT,
      }}
    >
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('organizing.eyebrow')}
          title={t('organizing.title')}
          subtitle={t('organizing.subtitle')}
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                height: '100%',
                background: PINK_TINT_04,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${ACCENT_PINK}`,
                borderRadius: 2,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: ACCENT_PINK,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Diversity3 sx={{ color: 'common.white', fontSize: 30 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: INK, fontWeight: 700, mb: 0.5, fontSize: '1.3rem' }}
                    >
                      {t('organizing.committee.title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_DIM, lineHeight: 1.6 }}>
                      {t('organizing.committee.host')}
                    </Typography>
                  </Box>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: PINK_TINT_10,
                        color: ACCENT_PINK,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.7 }}>
                      {t('organizing.committee.host')}
                    </Typography>
                  </Stack>
                </Paper>

                <Box>
                  <Eyebrow>{t('organizing.committee.rolesTitle')}</Eyebrow>
                  <Grid container spacing={1.25} sx={{ mt: 0.5 }}>
                    {roles.map((role, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            height: '100%',
                            borderRadius: 1.75,
                            background: BG_PRIMARY,
                            border: `1px solid ${HAIRLINE}`,
                          }}
                        >
                          <Stack direction="row" spacing={1.1} alignItems="flex-start">
                            <CheckCircle
                              sx={{ color: brand.hotPink, fontSize: 18, mt: 0.3, flexShrink: 0 }}
                            />
                            <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.65 }}>
                              {role}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                height: '100%',
                background: BLUE_TINT_04,
                border: `1px solid ${HAIRLINE}`,
                borderTop: `2px solid ${SOLID_BLUE}`,
                borderRadius: 2,
                boxShadow: CARD_SHADOW,
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: SOLID_BLUE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Gavel sx={{ color: 'common.white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: INK, fontWeight: 700, mb: 0.5, fontSize: '1.3rem' }}
                    >
                      {t('organizing.judges.title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_DIM, lineHeight: 1.6 }}>
                      {t('organizing.judges.intro')}
                    </Typography>
                  </Box>
                </Stack>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.7 }}>
                    {t('organizing.judges.intro')}
                  </Typography>
                </Paper>

                <Stack spacing={1.25}>
                  {rounds.map((round, i) => (
                    <Paper
                      key={i}
                      elevation={0}
                      sx={{
                        p: 1.75,
                        borderRadius: 1.75,
                        background: BG_PRIMARY,
                        border: `1px solid ${HAIRLINE}`,
                        borderLeft: `4px solid ${i % 2 === 0 ? SOLID_BLUE : ACCENT_PINK}`,
                      }}
                    >
                      <Chip
                        label={round.name}
                        size="small"
                        sx={{
                          background: i % 2 === 0 ? BLUE_TINT_08 : PINK_TINT_10,
                          color: i % 2 === 0 ? INK : ACCENT_PINK,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          mb: 1,
                          height: 22,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.65 }}>
                        {round.body}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Pledge */}
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 3,
            textAlign: 'center',
            background: BG_SOFT,
            border: `1px solid ${HAIRLINE}`,
            borderTop: `2px solid ${SOLID_BLUE}`,
            borderRadius: 2,
            boxShadow: CARD_SHADOW,
          }}
        >
          <Typography
            sx={{
              color: INK,
              fontStyle: 'italic',
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            {t('organizing.pledge')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Media Section — thư viện khoảnh khắc
// ═══════════════════════════════════════════════════════════════════════
function MediaSection() {
  const { t } = useTranslation()
  const categories = t('media.categories', { returnObjects: true }) as Array<{
    title: string
    description?: string
  }>
  const testimonials = t('media.testimonials', { returnObjects: true }) as Array<{
    quote: string
    author: string
  }>
  const activeTestimonial = 0

  const categoryIcons = [Visibility, Diversity3, Groups, EmojiEvents, Article, Gavel]

  return (
    <Box id="media" sx={{ py: { xs: 6, sm: 9 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('media.eyebrow')}
          title={t('media.title')}
          subtitle={t('media.subtitle')}
          align="center"
        />

        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: `1px solid ${HAIRLINE}`,
            borderTop: `2px solid ${SOLID_BLUE}`,
            background: BG_SOFT,
            boxShadow: CARD_SHADOW,
            textAlign: 'center',
          }}
        >
          <PlayCircleOutline sx={{ color: INK, fontSize: 48, mb: 1.5 }} />
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.15rem', mb: 0.75 }}>
            {t('media.recapTitle')}
          </Typography>
          <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 720, mx: 'auto' }}>
            {t('media.recapDescription')}
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {categories.map((category, i) => {
            const IconCmp = categoryIcons[i % categoryIcons.length]
            return (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    border: `1px solid ${HAIRLINE}`,
                    borderTop: `2px solid ${i % 2 === 0 ? SOLID_BLUE : ACCENT_PINK}`,
                    borderRadius: 2,
                    background: i % 2 === 0 ? BG_PRIMARY : PINK_TINT_04,
                    boxShadow: CARD_SHADOW,
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      borderColor: i % 2 === 0 ? SOLID_BLUE : ACCENT_PINK,
                      boxShadow: CARD_SHADOW_HOVER,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16/9',
                      background: 'rgba(20,78,140,0.1)',
                      border: `1px solid rgba(20,78,140,0.12)`,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <IconCmp sx={{ color: SOLID_BLUE, fontSize: 48, opacity: 0.95 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ color: INK, fontWeight: 700, fontSize: '1.05rem', mb: 1 }}
                  >
                    {category.title}
                  </Typography>
                  {category.description ? (
                    <Typography variant="body2" sx={{ color: TEXT_DIM, lineHeight: 1.65 }}>
                      {category.description}
                    </Typography>
                  ) : null}
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        {testimonials.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              mt: 5,
              p: { xs: 3, md: 3.5 },
              maxWidth: 760,
              mx: 'auto',
              border: `1px solid ${HAIRLINE}`,
              borderTop: `2px solid ${ACCENT_PINK}`,
              background: BG_PRIMARY,
              borderRadius: 2,
              boxShadow: CARD_SHADOW,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: INK, fontStyle: 'italic', lineHeight: 1.8, fontSize: '1rem' }}>
              &ldquo;{testimonials[activeTestimonial]?.quote}&rdquo;
            </Typography>
            <Typography
              sx={{
                color: TEXT_DIM,
                display: 'block',
                mt: 1.5,
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: 0.3,
              }}
            >
              — {testimonials[activeTestimonial]?.author}
            </Typography>
          </Paper>
        ) : null}
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// FAQ Section — accordion with search
// ═══════════════════════════════════════════════════════════════════════
function FAQSection() {
  const { t } = useTranslation()
  const topics = t('faq.topics', { returnObjects: true }) as string[]
  const items = t('faq.items', { returnObjects: true }) as Array<{
    question: string
    answer: string
  }>
  const [query, setQuery] = useState('')
  const normalizedQuery = normalizeSearchText(query)

  const filtered = useMemo(() => {
    if (!normalizedQuery) return items
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
    return items.filter((item) => {
      const haystack = `${normalizeSearchText(item.question)} ${normalizeSearchText(item.answer)}`
      return tokens.every((token) => haystack.includes(token))
    })
  }, [normalizedQuery, items])

  return (
    <Box id="faq" sx={{ py: { xs: 6, sm: 9 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('faq.eyebrow')}
          title={t('faq.title')}
          subtitle={t('faq.subtitle')}
          align="center"
        />

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          {topics.map((topic) => (
            <Chip
              key={topic}
              label={topic}
              sx={{
                fontWeight: 700,
                color: INK,
                background: BG_PRIMARY,
                border: `1px solid ${HAIRLINE}`,
              }}
            />
          ))}
        </Stack>

        <TextField
          variant="outlined"
          type="search"
          size="small"
          fullWidth
          placeholder={t('faq.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          sx={{
            display: 'block',
            maxWidth: 520,
            mx: 'auto',
            mb: 4,
            '& .MuiOutlinedInput-root': {
              background: BG_PRIMARY,
              borderRadius: 999,
              boxShadow: '0 8px 18px rgba(20,78,140,0.04)',
              '& fieldset': {
                borderColor: BLUE_TINT_14,
                borderWidth: 1.5,
              },
              '&:hover fieldset': {
                borderColor: SOLID_BLUE,
              },
              '&.Mui-focused fieldset': {
                borderColor: SOLID_BLUE,
                borderWidth: 1.5,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: SOLID_BLUE, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          {filtered.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('faq.emptyState')}
            </Alert>
          ) : (
            filtered.map((item, i) => (
              <Accordion
                key={i}
                disableGutters
                square={false}
                elevation={0}
                sx={{
                  mb: 1.5,
                  border: `1px solid ${BLUE_TINT_14}`,
                  borderRadius: 2,
                  background: BG_PRIMARY,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    borderColor: SOLID_BLUE,
                    boxShadow: '0 8px 18px rgba(20,78,140,0.05)',
                    background: BG_PRIMARY,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: INK }} />}
                  sx={{ px: 2.5, py: 0.5 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: INK, fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: TEXT_DIM, lineHeight: 1.7, whiteSpace: 'pre-line' }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════════════════════════
export default function PublicLandingPage() {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <Box sx={{ minHeight: '100dvh', background: BG_PRIMARY }}>
      <TopBar isAuthenticated={isAuthenticated} scrolled={scrolled} />
      <HeroSection isAuthenticated={isAuthenticated} />
      <StatsSection />
      <OverviewSection />
      <TimelineSection />
      <ScheduleSection />
      <RulesSection />
      <AwardsSection />
      <OrganizingSection />
      <ResultsSection />
      <PublicTeamsSection />
      <MediaSection />
      <PartnersSection />
      <FAQSection />
      <ContactSection />
      <FooterSection />
    </Box>
  )
}
